import { Inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { SiteContext, SiteContextUrlSerializer, WindowRef } from '@spartacus/core'
import {BehaviorSubject, Observable, of, Subject} from 'rxjs'
import { take } from 'rxjs/operators'
import { EbLocalStorageKey } from '../../../configuration/models/eb-local-storage.model'
import { EbBaseSite, EbSiteContextFallbackValues } from '../interfaces/eb-site-context.interface'
import { EbBaseSiteService } from './eb-base-site.service'
import { EbSiteContextComponentService } from './eb-site-context-component.service'

@Injectable({ providedIn: 'root' })
export class EbCountryService implements SiteContext<string> {
  allCountries: string[] = []
  siteContext = []
  activeCountry$: BehaviorSubject<string> = new BehaviorSubject(undefined)
  countryChanged$: Subject<void> = new Subject<void>();


  constructor(
    protected winRef: WindowRef,
    protected ebSiteContextComponentService: EbSiteContextComponentService,
    protected baseSiteService: EbBaseSiteService,
    @Inject('Window') protected window
  ) {
    this.ebSiteContextComponentService
      .getItems()
      .pipe(take(1))
      .subscribe(items => {
        if (items) {
          items.forEach(item => this.allCountries.push(item.country.isocode))
          this.siteContext = items
          this.setActive(this.getCountryFromUrl())
        }
      })
  }

  static initialCountryHandling(
    countryService: EbCountryService,
    router: Router,
    siteContextUrlSerializer: SiteContextUrlSerializer
  ) {
    return () => {
      if (countryService.getCountryFromUrl()) {
        countryService.setActive(countryService.getCountryFromUrl())
      } else {
        const urlTree = router.createUrlTree([])
        const url = siteContextUrlSerializer.serialize(urlTree)
        router.navigateByUrl(url)
      }
    }
  }

  getActive(): Observable<string> {
    if (this.activeCountry$.value === undefined) {
      this.activeCountry$.next(this.getCountryFromUrl())
    }
    return this.activeCountry$
  }

  setActive(country: string): void {
    this.baseSiteService.getAll().subscribe((all: EbBaseSite[]) => {
      const baseSiteForCountry = this.checkCountryValidityAndGiveBaseSite(country, all)
      if (baseSiteForCountry) {
        this.activeCountry$.next(country)
        this.baseSiteService.setActive(baseSiteForCountry)
        this.winRef.localStorage.setItem(EbLocalStorageKey.COUNTRY_KEY, country)
        this.countryChanged$.next();
      } else {
        // fallback
        this.activeCountry$.next(EbSiteContextFallbackValues.COUNTRY)
        this.baseSiteService.setActive(EbSiteContextFallbackValues.BASESITE)
      }
    })
  }

  checkCountryValidityAndGiveBaseSite(country: string, allSites: EbBaseSite[]): string {
    return allSites.find(site => site.country?.isocode === country)?.uid
  }

  getAll(): Observable<string[]> {
    return of(this.allCountries)
  }

  getBaseSiteForCountry(country: string) {
    const activeContext = this.siteContext.find(context => context.country.isocode === country)
    return activeContext?.uid
  }

  getCountryFromUrl(): string {
    const urlParts = this.winRef.location.href.split('/')
    // verify that first parameter is existing and a country (upper case) and not a language(lower case)
    if (!urlParts[3] || urlParts[3] === urlParts[3].toUpperCase()) {
      return urlParts[3]
    } else if (urlParts[3]) {
      return urlParts[3].toUpperCase()
    } else {
      // fallback
      return this.checkForStoredCountryOrFallback()
    }
  }

  checkForStoredCountryOrFallback(): string {
    const storedCountry = this.winRef.nativeWindow.localStorage.getItem(
      EbLocalStorageKey.COUNTRY_KEY
    )
    return storedCountry || (EbSiteContextFallbackValues.COUNTRY as string)
  }
}
